import {
	faBuilding,
	faChartLine,
	faChevronLeft,
	faChevronRight,
	faFileAlt,
	faFileInvoiceDollar,
	faHome,
	faRuler,
	faShieldHalved,
	faTags,
	faTicketAlt,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { Alert, Nav, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ErrorAlert from "../../components/ErrorAlert";
import NoPermissionAlert from "../../components/NoPermissionAlert";
import useProjectPermission from "../../hooks/useProjectPermission";
import { isProjectAccessDenied } from "../../utils/isProjectAccessDenied";
import {
	clearState,
	setProjectDetails,
} from "../../store/slices/projectDetailsSlice";
import { useGetProjectByIdQuery } from "../../store/services/projectsService";
import {
	getNavTabVisibility,
	getProjectRouteAccess,
} from "./projectRouteAccess";
import "./project-details.scss";

const NAV_GROUPS = [
	{
		groupKey: null,
		items: [
			{ id: "overview", icon: faHome, translationKey: "common.overview" },
		],
	},
	{
		groupKey: "nav.group-project",
		items: [
			{ id: "planning", icon: faRuler, translationKey: "planning.title" },
			{
				id: "cost-tracking",
				icon: faChartLine,
				translationKey: "costTracking.title",
			},
			{
				id: "ticket-categories",
				icon: faTags,
				translationKey: "projects.ticket-categories",
			},
			{ id: "lv", icon: faFileAlt, translationKey: "lv.lv" },
			{
				id: "invoice",
				icon: faFileInvoiceDollar,
				translationKey: "invoice.invoice",
			},
		],
	},
	{
		groupKey: "nav.group-tasks",
		items: [
			{
				id: "tickets",
				icon: faTicketAlt,
				translationKey: "tickets.title",
			},
		],
	},
	{
		groupKey: "nav.group-team",
		items: [
			{
				id: "members",
				icon: faUsers,
				translationKey: "projects.members.title",
			},
			{
				id: "permission-presets",
				icon: faShieldHalved,
				translationKey: "projects.permission-presets.title",
			},
			{
				id: "companies",
				icon: faBuilding,
				translationKey: "projects.companies.title",
			},
		],
	},
];

const selectProjectDetailsState = (state) => state.projectDetails;

const selectProjectDetailsData = createSelector(
	[selectProjectDetailsState],
	(projectDetails) => ({
		project: projectDetails.project,
	}),
);

function ProjectDetailsLayout() {
	const { t } = useTranslation();
	const { projectId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const { project: projectDetails } = useSelector(selectProjectDetailsData);
	const {
		data: projectData,
		isLoading: loading,
		error,
	} = useGetProjectByIdQuery(projectId, { skip: !projectId });

	useEffect(() => {
		if (projectData) dispatch(setProjectDetails(projectData));
	}, [projectData, dispatch]);

	const {
		can,
		isLoading: memberPermissionLoading,
		isForbidden,
		isError: memberPermissionError,
		ticketViewLevel,
		ticketCategoryIds,
	} = useProjectPermission();

	const [isNavCollapsed, setIsNavCollapsed] = useState(true);

	const isLocalProject = !projectDetails?.capmoId;

	const permissionContext = useMemo(
		() => ({
			can,
			ticketViewLevel,
			ticketCategoryIds,
			isLocalProject,
		}),
		[can, ticketViewLevel, ticketCategoryIds, isLocalProject],
	);

	const routeAllowed = useMemo(() => {
		if (memberPermissionLoading) return true;
		return getProjectRouteAccess(
			location.pathname,
			projectId,
			permissionContext,
		);
	}, [
		location.pathname,
		projectId,
		permissionContext,
		memberPermissionLoading,
	]);

	useEffect(() => {
		return () => {
			dispatch(clearState());
		};
	}, [dispatch, projectId]);

	const toggleNavCollapse = () => {
		setIsNavCollapsed(!isNavCollapsed);
	};

	if (loading) {
		return (
			<div className="text-center p-5">
				<Spinner animation="border" role="status">
					<span className="visually-hidden">{t("common.loading")}</span>
				</Spinner>
				<p className="mt-3">{t("common.loading")}</p>
			</div>
		);
	}

	if (error) {
		const errData = error?.data ?? error;
		if (isProjectAccessDenied(errData)) {
			return (
				<NoPermissionAlert
					title={t("projects.no-access-title")}
					message={t("projects.no-access")}
					showBackButton
				/>
			);
		}

		const errorMessage =
			typeof errData === "string"
				? t(errData, { defaultValue: errData })
				: t(errData?.originalMessage || errData?.message, {
						defaultValue: errData?.message,
					});

		return <ErrorAlert message={errorMessage} />;
	}

	if (!projectDetails) {
		return (
			<Alert variant="warning" className="m-4">
				<Alert.Heading>{t("common.warning")}</Alert.Heading>
				<p>{t("common.no-data")}</p>
			</Alert>
		);
	}

	if (memberPermissionLoading) {
		return (
			<div className="text-center p-5">
				<Spinner animation="border" role="status">
					<span className="visually-hidden">{t("common.loading")}</span>
				</Spinner>
				<p className="mt-3">{t("common.loading")}</p>
			</div>
		);
	}

	if (isForbidden) {
		return (
			<NoPermissionAlert
				title={t("projects.no-access-title")}
				message={t("projects.no-access")}
				showBackButton
			/>
		);
	}

	if (memberPermissionError) {
		return <ErrorAlert message={t("common.error")} />;
	}

	return (
		<div className="project-details-layout d-flex">
			<div
				className={`nav-container d-flex flex-column bg-light border-end shadow-sm ${
					isNavCollapsed ? "collapsed" : ""
				}`}
				style={{ width: isNavCollapsed ? "60px" : "240px" }}
			>
				<div className="nav-toggle-btn" onClick={toggleNavCollapse}>
					<FontAwesomeIcon
						icon={isNavCollapsed ? faChevronRight : faChevronLeft}
						size="sm"
					/>
				</div>

				<Nav
					className="flex-column p-3 pt-5 mt-3"
					style={{ width: isNavCollapsed ? "60px" : "240px" }}
				>
					{NAV_GROUPS.map((group, groupIndex) => {
						const groupItems = group.items.filter((item) => {
							if (memberPermissionLoading) return true;
							return getNavTabVisibility(item.id, permissionContext);
						});
						if (groupItems.length === 0) return null;

						return (
							<div key={groupIndex} className="nav-group">
								{groupIndex > 0 &&
									(isNavCollapsed ? (
										<hr className="nav-group-divider" />
									) : (
										<div className="nav-group-label">
											{t(group.groupKey)}
										</div>
									))}
								{groupItems.map((item) => {
									const isActive =
										(item.id === "overview" &&
											location.pathname ===
												`/projects/${projectId}`) ||
										location.pathname.includes(item.id);

									const navLink = (
										<Nav.Link
											onClick={() =>
												item.id === "overview"
													? navigate(`/projects/${projectId}`)
													: navigate(
															`/projects/${projectId}/${item.id}`,
														)
											}
											className={`project-nav-link d-flex align-items-center ${isActive ? "active" : ""}`}
										>
											<div
												className={`${isNavCollapsed ? "mx-auto" : ""}`}
											>
												<FontAwesomeIcon
													icon={item.icon}
													className={
														isNavCollapsed ? "" : "me-2"
													}
													fixedWidth
												/>
											</div>
											<span className="nav-link-text">
												{t(item.translationKey)}
											</span>
										</Nav.Link>
									);

									return isNavCollapsed ? (
										<OverlayTrigger
											key={item.id}
											placement="right"
											overlay={
												<Tooltip id={`tooltip-${item.id}`}>
													{t(item.translationKey)}
												</Tooltip>
											}
										>
											<div>{navLink}</div>
										</OverlayTrigger>
									) : (
										<div key={item.id}>{navLink}</div>
									);
								})}
							</div>
						);
					})}
				</Nav>
			</div>

			<div className="flex-grow-1 project-details-content">
				<div className="project-details-scroll-container">
					{routeAllowed ? <Outlet /> : <NoPermissionAlert />}
				</div>
			</div>
		</div>
	);
}

export default ProjectDetailsLayout;
